import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState
} from 'react';
import { PageLoader } from 'src/components/shared/Loader/PageLoader';
import { getRequest } from 'src/store/api-handler';
import { CurrentUserDto, UserRole } from 'src/store/users/user';
import { AuthorizationError } from '../Error/AuthorizationError';
import { LoginError } from '../Error/LoginError';
import {
  clearTokens,
  initializeKeycloak,
  keycloak,
  setTokens
} from './keycloak.config';

export type AuthContextProps = {
  user: CurrentUserDto;
  isAuthenticated: boolean;
  signin: () => void;
  signout: () => void;
};

export const AuthContext = createContext<AuthContextProps>(
  {} as AuthContextProps
);

export const useAuth = () => {
  return useContext(AuthContext);
};

export const isUserAdmin = (user: CurrentUserDto) => {
  return user?.roles?.some(r => r === UserRole.Admin);
};

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [isAuthenticating, setIsAuthenticating] = useState(true);
  const [authError, setAuthError] = useState<string | null>(null);
  const [user, setUser] = useState<CurrentUserDto>({} as CurrentUserDto);

  useEffect(() => {
    const setCurrentUser = async () => {
      const { ok, data } = await getRequest<CurrentUserDto>('/users/current');

      if (ok) {
        setUser(data);
      }
      setIsAuthenticating(false);
    };

    setIsAuthenticating(true);
    initializeKeycloak(
      isAuthenticated => {
        if (isAuthenticated) {
          setTokens(keycloak.token!, keycloak.refreshToken!);
          setCurrentUser();
        } else {
          setIsAuthenticating(false);
        }
      },
      error => {
        console.error('Authentication failed:', error);
        setAuthError(error);
        setIsAuthenticating(false);
      }
    );
  }, []);

  const signout = () => {
    clearTokens();
    keycloak.logout();
  };

  const signin = () => {
    keycloak.login();
  };

  const isAuthenticated = !!user?.id;
  const hasRoles = user?.roles?.length > 0;

  // Show auth error with retry option
  if (authError) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <h2>Authentication Error</h2>
        <p>{authError}</p>
        <button
          onClick={() => {
            sessionStorage.clear();
            localStorage.removeItem('lims_token');
            localStorage.removeItem('lims_refresh_token');
            window.location.href = '/';
          }}
          style={{
            padding: '0.5rem 1rem',
            marginTop: '1rem',
            cursor: 'pointer'
          }}
        >
          Clear session and retry
        </button>
      </div>
    );
  }

  return (
    <AuthContext.Provider
      value={{
        user: user,
        isAuthenticated: isAuthenticated,
        signin: signin,
        signout: signout
      }}
    >
      {isAuthenticating && <PageLoader />}
      {!isAuthenticated && !isAuthenticating && <LoginError />}
      {isAuthenticated && !hasRoles && <AuthorizationError />}
      {isAuthenticated && hasRoles && children}
    </AuthContext.Provider>
  );
};
